import { IonItem, IonText } from '@ionic/react';
import { Form } from 'formik';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import i18n from '../../i18n';
import { openLink } from '../../util/link-utils';
import { IonFormField } from '../form/ion-form.field';
import {
  EventIntegrationFormParams,
  EventIntegrationTestResult,
} from './event-interfaces';

export const slackIntegrationSchema = yup.object().shape({
  label: yup.string().required('*'),
  config: yup.object({
    settings: yup.object({
      channel: yup.string().required('*'),
      url: yup
        .string()
        .required('*')
        .matches(
          /https:\/\/hooks\.slack\.com\/services\/T[a-zA-Z0-9]+\/B[a-zA-Z0-9]+\/[a-zA-Z0-9]+/,
          {
            message: i18n.t('common:event_integration_test_slack_url_invalid'),
          }
        ),
    }),
  }),
});

export const testSlackIntegration = async (
  formData: yup.InferType<typeof slackIntegrationSchema>
): Promise<EventIntegrationTestResult> => {
  const url: string | undefined = formData?.config?.settings?.url;

  if (url === undefined) {
    return { success: false };
  }

  const result = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    body: JSON.stringify({
      text: i18n.t('common:event_integration_test_slack_greeting'),
    }),
  });

  if (result.ok) {
    return {
      success: true,
      successMessage: 'common:event_integration_test_slack_succeeded',
    };
  } else {
    return {
      success: false,
      errorMessage: 'common:event_integration_test_slack_failed',
    };
  }
};

const SlackIntegrationForm = (
  props: EventIntegrationFormParams
): ReactElement => {
  const { readOnly } = props;
  const { t } = useTranslation();

  return (
    <Form>
      <IonItem lines="none">
        <IonText color="medium" style={{ fontSize: 'small' }}>
          {`${t('event_integration_slack_link_message_1')} `}
          <a
            href="https://api.slack.com/messaging/webhooks"
            target="_blank"
            rel="noreferrer"
            onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
              e.stopPropagation();
              e.preventDefault();
              openLink('https://api.slack.com/messaging/webhooks');
            }}
          >
            Slack
          </a>
          {` ${t('event_integration_slack_link_message_2')}`}
        </IonText>
      </IonItem>
      <IonFormField
        label={t('event_integration_name')}
        name="label"
        inputProps={{
          disabled: readOnly,
        }}
      />
      <IonFormField
        label={t('event_integration_slack_url')}
        name="config.settings.url"
        inputProps={{
          placeholder:
            'https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXX...',
          disabled: readOnly,
        }}
      />
      <IonFormField
        label={t('event_integration_slack_channel')}
        name="config.settings.channel"
        inputProps={{
          disabled: readOnly,
        }}
      />
    </Form>
  );
};

export { SlackIntegrationForm };
